import PropTypes from 'prop-types';
import { Typography, Stack, Card } from '@mui/material';
import { useSelector } from 'react-redux';
import Scrollbar from '../../../reusable-components/scrollbar';
import Iconify from '../../../reusable-components/iconify';
import { fDate } from '../../../utils/formatTime';
import ReportsSideBarGroup from './ReportSideBarGroup';

ReportsSideBar.propTypes = {
  formValues: PropTypes.object,
};

export default function ReportsSideBar({ formValues }) {
  const reportSpecificationOptions = useSelector((state) => state.reports.data.reportSpecificationOptions) || [];
  const hasOption = (name) => reportSpecificationOptions.some((o) => o.name === name);
  const yesNo = (value) => (value ? 'Yes' : 'No');
  const formatRange = (start, end) => (start && end ? `${fDate(start, 'MM/dd/yyyy')} - ${fDate(end, 'MM/dd/yyyy')}` : '');
  const getFirstValue = (value) => (Array.isArray(value) ? value[0]?.value : value);
  const month = getFirstValue(formValues.month);
  const year = getFirstValue(formValues.year);
  const monthName = typeof month === 'number' && month >= 1 && month <= 12 ? fDate(new Date(2000, month - 1), 'MMMM') : '';
  const monthYear = `${monthName}${monthName && year ? ', ' : ''}${year ?? ''}`;

  const checkboxes = [
    { name: 'PriceChangesOnly', title: 'Show Only Items with Price Change:', value: formValues.priceChangesOnly },
    { name: 'PendingOnly', title: 'Only Include Pending Invoices:', value: formValues.pendingOnly },
    { name: 'ExcludeTicketData', title: 'Exclude Ticket Data:', value: formValues.excludeTicketData },
    { name: 'IncludeChildFacs', title: 'Include Child Facilities:', value: formValues.includeChildFacs },
    {
      name: 'IncludeFacsWithoutGroup',
      title: 'Include Facilities not Associated with a Group:',
      value: formValues.includeFacsWithoutGroup,
    },
  ];

  return (
    <Scrollbar sx={{ height: 'calc(100vh - 200px)' }}>
      <Card sx={{ p: 1, width: '100%', overflow: 'auto' }}>
        <Stack direction="column">
          {formValues.selectedReport && (
            <>
              <Typography sx={{ fontWeight: 'bold', clear: 'both' }} variant="subtitle1" color="info.main">
                <Iconify
                  sx={{ mr: 0.5, display: 'inline' }}
                  icon="mingcute:paper-line"
                  width={16}
                  height={16}
                  color="secondary"
                />
                {formValues.selectedReport?.name}
              </Typography>
            </>
          )}
          <ReportsSideBarGroup
            values={formValues.dateValues}
            title="Dates:"
            icon={'mingcute:calendar-2-line'}
            getValue={(value) =>
              `${value[0]?.month.shortName} ${value[0]?.day} ${value[0].year}  -  ${value[1].month.shortName} ${value[1].day} ${value[1].year}`
            }
          />
          <ReportsSideBarGroup
            inline
            values={
              hasOption('DateFilter') || hasOption('CustomDateRange')
                ? formatRange(formValues.startDate, formValues.endDate)
                : ''
            }
            title="Date Range:"
            icon={'mingcute:calendar-2-line'}
            getValue={(value) => value}
          />
          <ReportsSideBarGroup
            inline
            values={
              hasOption('DateBetween') ? formatRange(formValues.startBetweenDate, formValues.endBetweenDate) : ''
            }
            title="Take From Between:"
            icon={'mingcute:calendar-2-line'}
            getValue={(value) => value}
          />
          <ReportsSideBarGroup
            inline
            values={hasOption('Take') && formValues.take ? `${formValues.take}` : ''}
            title="Invoices Checked:"
            icon={'line-md:list'}
            getValue={(value) => value}
          />
          <ReportsSideBarGroup
            inline
            values={hasOption('Skip') && formValues.skip ? `${formValues.skip}` : ''}
            title="Invoices Skipped:"
            icon={'line-md:list'}
            getValue={(value) => value}
          />
          <ReportsSideBarGroup
            values={formValues.companies}
            title="Groups:"
            icon={'mingcute:group-3-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            values={formValues.facilities}
            title="Facilities:"
            icon={'mingcute:hotel-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            values={formValues.categories}
            title="Categories:"
            icon={'mingcute:hexagons-2-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            values={formValues.subcategories}
            title="Subcategories:"
            icon={'mingcute:filter-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            values={formValues.vendors}
            title="Vendors:"
            icon={'mingcute:user-star-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            values={formValues.rentalVendor}
            title="Rental Vendor:"
            icon={'mingcute:user-star-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            values={formValues.serialNumbers}
            title="Serial Numbers:"
            icon={'line-md:list'}
            getValue={(value) => value}
          />
          <ReportsSideBarGroup
            values={formValues.transportStatuses}
            title="Statuses:"
            icon={'mingcute:list-check-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            values={formValues.timeFrameFilters}
            title="Time Frames:"
            icon={'mingcute:time-line'}
            getValue={(value) => value.label}
          />
          <ReportsSideBarGroup
            inline
            values={hasOption('Month') || hasOption('Year') ? monthYear : ''}
            title="Month and Year:"
            icon={'stash:data-date'}
            getValue={(value) => value}
          />
          {hasOption('UseBudgetDate') && (
            <ReportsSideBarGroup
              inline
              values={formValues.useBudgetDate ? 'Budget Date' : 'Order Approved Date'}
              title="Filter Report By:"
              icon={'mingcute:calendar-2-line'}
              getValue={(value) => value}
            />
          )}
          {checkboxes
            .filter((checkbox) => hasOption(checkbox.name))
            .map((checkbox) => (
              <ReportsSideBarGroup
                inline
                key={checkbox.name}
                values={yesNo(checkbox.value)}
                title={checkbox.title}
                icon={'mingcute:check-circle-line'}
                getValue={(value) => value}
              />
            ))}
          {formValues.selectedReport?.id === 20 && (
            <ReportsSideBarGroup
              inline
              values={yesNo(formValues.includePDFs)}
              title="Include PDFs:"
              icon={'mingcute:check-circle-line'}
              getValue={(value) => value}
            />
          )}
        </Stack>
      </Card>
    </Scrollbar>
  );
}
